import React from "react";
import DownArrow from "./DownArrow";
import { IconContainer, IconDropdown, IconLabel, IconSVG } from "./Icon";
import useBreakpoints from "~/hooks/useBreakpoints";
import { FLASH_SIGNIN_FAILURE } from "~/constants";
import { useFetcher } from "@remix-run/react";
import FlashMessage from "../FlashMessage";
import { useHydrated } from "~/hooks/useHydrated";

const ErrorMessage = () => <FlashMessage {...FLASH_SIGNIN_FAILURE} />;

export const DropdownContents: React.FC = () => {
  const isHydrated = useHydrated();
  const fetcher = useFetcher<{ errors: boolean }>();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [referrer, setReferrer] = React.useState("");
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  React.useEffect(() => {
    if (!isHydrated || referrer) return;
    setReferrer(window.document.referrer);
  }, [isHydrated, referrer]);

  React.useEffect(() => {
    if (!fetcher.data?.errors) return;
    setIsError(true);
  }, [fetcher.data?.errors]);

  const handlePasswordIconClick = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsError(false);

    if (event.target.name === "email") {
      setEmail(event.target.value);
    } else if (event.target.name === "password") {
      setPassword(event.target.value);
    }
  };

  const handleSubmit = async () => {
    setPassword("");
  };

  return (
    <fetcher.Form
      method="post"
      action="/resources/sign-in"
      className="flex flex-col gap-3.5 px-7 pb-8 pt-3"
      onSubmit={handleSubmit}
    >
      <input
        hidden={true}
        type="text"
        name="referrer"
        defaultValue={referrer}
      />
      {isError ? <ErrorMessage /> : null}
      <div className="flex flex-col gap-1">
        <label
          className="text-sm text-blue900"
          htmlFor="email"
        >
          Email
        </label>
        <input
          id="email"
          className="h-8 rounded border border-black p-2"
          type="text"
          name="email"
          value={email}
          onChange={handleInputChange}
          placeholder="Email"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-sm text-blue900"
          htmlFor="password"
        >
          Password
        </label>
        <div className="relative flex">
          <input
            id="password"
            className="h-8 w-full rounded border border-black p-2"
            type={isPasswordVisible ? "text" : "password"}
            name="password"
            onChange={handleInputChange}
            value={password}
            placeholder="Password"
          />
          <button
            type="button"
            aria-label="Toggle password visibility"
            className="absolute right-6 top-1.5 block"
            onClick={handlePasswordIconClick}
          >
            <svg
              className="fill-gray"
              width="20"
              height="20"
            >
              <use
                xlinkHref={`/frontend-assets/images/icons.svg#eye-${isPasswordVisible ? "open" : "closed"}`}
              />
            </svg>
          </button>
        </div>
      </div>
      <div className="mb-1 flex justify-between pr-4">
        <div className="flex flex-row-reverse items-center gap-1">
          <label
            className="text-sm text-blue900"
            htmlFor="remember"
          >
            Remember me
          </label>
          <input
            id="remember"
            type="checkbox"
            name="remember"
          />
        </div>
        <a
          className="text-sm text-blue400 underline"
          href="/users/password/new"
        >
          Forgot password
        </a>
      </div>
      <p className="mb-3 text-gray">
        By signing into your account, you consent to ShirtSpace&apos;s{" "}
        <a
          className="text-sm text-blue900 underline"
          href="/privacy-policy"
        >
          Privacy Policy
        </a>
        .
      </p>
      <div className="align-center flex gap-3">
        <button
          type="submit"
          className="h-8 w-auto self-start rounded-md bg-blue px-4  text-lg uppercase text-white hover:bg-blue200"
        >
          Sign In
        </button>
        <p className="self-center text-sm text-gray">
          <span>Or</span>{" "}
          <a
            href="/users/sign_up"
            className="text-blue900 underline"
          >
            Create an account
          </a>
        </p>
      </div>
    </fetcher.Form>
  );
};

interface Props {
  color?: "gray" | "white";
  hasSession: boolean;
  isDropdownActive: boolean;
  setIsDropdownActive: (value: boolean) => void;
}

const SignIn: React.FC<Props> = ({
  color = "gray",
  hasSession,
  isDropdownActive,
  setIsDropdownActive,
}) => {
  const { isMobile, isDesktop } = useBreakpoints();

  const handleMouseEnter = () => {
    if (isMobile || hasSession) return;
    setIsDropdownActive(true);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) return;

    if (!(event.target instanceof HTMLElement)) {
      setIsDropdownActive(false);
      return;
    }

    /*
     * If the mouse is still within the bounds of the dropdown, do nothing.
     * This is necessary because the mouseleave event may be in some cases
     * triggered by an element injected by a browser extension
     * (e.g. password managers)
     */
    const isMenu = event.target.closest("[role=menu]");

    if (!isMenu) {
      setIsDropdownActive(false);
      return;
    }

    const element = isMenu;

    const { top, bottom, left, right } = element.getBoundingClientRect();
    const { x, y } = event.nativeEvent;

    if (x >= left && x <= right && y >= top && y <= bottom) return;

    setIsDropdownActive(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (isDesktop) {
      window.location.href = hasSession ? "/account" : "/users/sign_in";
      return;
    } else if (isMobile && hasSession) {
      window.location.href = "/account";
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsDropdownActive(!isDropdownActive);
  };

  const handleCloseClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    event.preventDefault();
    setIsDropdownActive(false);
  };
  return (
    <IconContainer
      href={hasSession ? "/account" : "/users/sign_in"}
      title={hasSession ? "Account" : "Sign In"}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <IconSVG className={`${color === "white" ? "md:fill-white" : ""}`}>
        <g>
          <path d="M10.66,10.95c10.43,0,10.66,7.38,10.66,7.38H0s.23-7.38,10.66-7.38Z" />
          <circle
            cx="10.66"
            cy="4.5"
            r="4.5"
          />
        </g>
      </IconSVG>
      <IconLabel className={`${color === "white" ? "md:text-[#ffffff]" : ""}`}>
        {hasSession ? "Account" : "Sign In"}
        {hasSession ? null : (
          <DownArrow className={color === "white" ? "fill-white" : ""} />
        )}
      </IconLabel>
      <IconDropdown
        title="Sign in"
        isActive={!hasSession && isDropdownActive}
        onCloseClick={handleCloseClick}
      >
        <DropdownContents />
      </IconDropdown>
    </IconContainer>
  );
};

export default SignIn;
